import { slugify } from "./string";

/**
 * Daytona Agent UI sandbox file layout contract.
 *
 * Naming: `*_DIR` constants are workspace-relative (e.g. `dev/website`);
 * `*_PATH` constants are absolute sandbox paths (e.g. `/workspace/dev/website`);
 * `*_SEGMENTS` constants are Drive row paths — the tree names a namespace walk
 * traverses, including structural roots that never appear in a sandbox path.
 *
 * These mirror a layout produced by the snapshot image, so a change here is a
 * multi-artifact migration (pinned by `sandboxPaths.unit.spec.ts`).
 */
function trimPathSlashes(pathSegment: string): string {
  return pathSegment.replace(/^\/+|\/+$/gu, "");
}

// String join only: trims slashes between segments and drops empty segments.
// Does not normalize `..`, reject absolute segments, or dedupe an existing
// workspace prefix — sanitize untrusted segments before calling.
export function joinSandboxPath(...segments: string[]): string {
  const normalizedSegments = segments
    .map((segment, index) => {
      return index === 0
        ? segment.replace(/\/+$/u, "")
        : trimPathSlashes(segment);
    })
    .filter((segment) => segment.length > 0);
  return normalizedSegments.join("/");
}

export const SANDBOX_WORKSPACE_PATH = "/workspace";

// Debug-only sentinel the coordinator writes and the publish tool reads to
// synthesize per-phase failures. Under /run (tmpfs) so it self-clears on restart.
export const SANDBOX_PUBLISH_FAULT_PATH = "/run/replo-publish-fault.json";
// Recovery metadata must outlive site-source corruption but self-clear when
// the sandbox restarts, matching the other debug-only state under /run.
export const SANDBOX_DEBUG_FAULT_RECOVERY_DIR =
  "/run/replo-debug-fault-recovery";
export function getSandboxFaultSentinelPath({ layer }: { layer: string }) {
  return `/run/fault-${layer}.json`;
}
// Per-session route selections for publish sessions. The coordinator writes a
// session's file before approving its publish permission and the publish tool
// reads it back, so a machine-initiated publish never depends on the model
// relaying the selection. One file per session so overlapping publishes cannot
// clobber each other. Under /run (tmpfs) so it self-clears on restart.
export const SANDBOX_PUBLISH_SELECTION_DIR = "/run/replo-publish-selection";
const OPENCODE_SESSION_ID_PATTERN = /^[A-Za-z0-9_-]+$/u;

export class UnsafeOpencodeSessionIdError extends Error {}

export function getSandboxPublishSelectionPath({
  opencodeSessionId,
}: {
  opencodeSessionId: string;
}) {
  if (!OPENCODE_SESSION_ID_PATTERN.test(opencodeSessionId)) {
    throw new UnsafeOpencodeSessionIdError(
      `Opencode session id is not a safe file name: ${opencodeSessionId}`,
    );
  }
  return `${SANDBOX_PUBLISH_SELECTION_DIR}/${opencodeSessionId}.json`;
}
// A v1 migration refine session's files, one directory per opencode session: the coordinator writes
// `session.json` (what to check), the `v1-migration-check` tool keeps `state.json` (where the loop is),
// and the agent writes `report/report.json`. Outside every site's working tree so none of it is committed or
// published; under /run like the publish selection, so a restarted sandbox starts the loop clean. The report
// has its own directory because opencode grants outside-directory writes per directory, and the agent must
// not be able to write the state the reminder and the admin page trust.
export const SANDBOX_V1_MIGRATION_DIR = "/run/replo-v1-migration";

export function getSandboxV1MigrationPaths({
  opencodeSessionId,
}: {
  opencodeSessionId: string;
}) {
  if (!OPENCODE_SESSION_ID_PATTERN.test(opencodeSessionId)) {
    throw new UnsafeOpencodeSessionIdError(
      `Opencode session id is not a safe file name: ${opencodeSessionId}`,
    );
  }
  const directory = `${SANDBOX_V1_MIGRATION_DIR}/${opencodeSessionId}`;
  return {
    directory,
    session: `${directory}/session.json`,
    state: `${directory}/state.json`,
    report: `${directory}/report/report.json`,
    // the site judge's scores (judge.json, fix-list.md, reference bundles) and the agent's notes on them
    judge: `${directory}/judge`,
    // the site judge's scores of the site as compiled, before the agent edited it, which the final judgement
    // must not fall below
    baseline: `${directory}/baseline.json`,
    notes: `${directory}/report/notes.json`,
    // the known issues filed from judge.json when the hand-off ended, so a later idle does not file them again
    issues: `${directory}/issues.json`,
    // what merging the session's repeated sections into shared ones did, written once it ran
    shared: `${directory}/shared.json`,
    // the site compile's shared props for the naming pass, and the names it chose
    namingContext: `${directory}/naming-context.json`,
    propNames: `${directory}/prop-names.json`,
  };
}
// NOTE (Yuxin, 2026-07-26): intentional-local-mode marker; must match
// DISABLED_MARKER in apps/daytona-snapshot/files-volume-probe.sh.
export const SANDBOX_FILES_VOLUME_DISABLED_MARKER_PATH =
  "/run/files-volume-disabled";
// NOTE (Yuxin, 2026-07-26): baked only into the snapshot image, so presence
// means "runs files-volume" — unlike /workspace, which CI containers have too.
export const SANDBOX_FILES_VOLUME_PROBE_SCRIPT_PATH =
  "/etc/s6/files-volume-probe.sh";
export const SANDBOX_DEV_CONTROL_SCRIPT_PATH = "/etc/s6/dev-control.sh";

// Top-level Drive mounts (files-fuse): `files` per-project Drive, `shared` shared-with-me, `project` project-scoped, `user` user-global.
export const SANDBOX_FILES_DIR = "files";
export const SANDBOX_SHARED_DIR = "shared";
export const SANDBOX_PROJECT_DIR = "project";
export const SANDBOX_USER_DIR = "user";

// Machine-canonical context (files-fuse namespace=system): user context is user-global at user/.context; project context is project-scoped at project/.context.
export const SANDBOX_USER_CONTEXT_DIR = joinSandboxPath(
  SANDBOX_USER_DIR,
  ".context",
);
export const SANDBOX_PROJECT_CONTEXT_DIR = joinSandboxPath(
  SANDBOX_PROJECT_DIR,
  ".context",
);

const sandboxAppsDirectory = "apps";
const sandboxBrandStudioDirectory = joinSandboxPath(
  sandboxAppsDirectory,
  "brand-studio",
);
// Project branding is project-scoped context shared by every member's sandbox,
// so it lives under project/.context.
const sandboxProjectBrandingDirectory = joinSandboxPath(
  SANDBOX_PROJECT_CONTEXT_DIR,
  "branding",
);

// Multi-site root: every site is a working tree at `dev/<directory>`. The
// folder name is derived and renameable — canonical identity is the
// `replo.siteId` pin in each tree's package.json, resolved at runtime
// (coordinator `SiteService.getDirectory` / `dev-control.sh dir <siteId>`).
// Build per-site paths with `joinSandboxPath(SANDBOX_DEV_PATH, directory)`.
export const SANDBOX_DEV_DIR = "dev";
export const SANDBOX_BRAND_STUDIO_DRAFTS_DIR = joinSandboxPath(
  sandboxBrandStudioDirectory,
  "drafts",
);
export const SANDBOX_INSIGHTS_DIR = joinSandboxPath(
  sandboxAppsDirectory,
  "insights",
);
export const SANDBOX_OPENCODE_DIR = ".opencode";
export const SANDBOX_OPENCODE_SKILLS_DIR = joinSandboxPath(
  SANDBOX_OPENCODE_DIR,
  "skills",
);
// opencode-distribution-relative paths of the deterministic registry CLIs:
// `scripts/opencode-distribution.ts` esbuild-bundles the `bin/` sources here,
// and coordinator RPCs `exec` the bundled artifacts at these paths.
export const UPLOAD_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH =
  "bin/run-upload-registry-items.mjs";
export const INSTALL_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH =
  "bin/run-install-registry-items.mjs";
export const PAGE_AUDIT_SCAN_CLI_DIST_RELATIVE_PATH =
  "bin/run-page-audit-scan.mjs";
export const SITE_PAGE_CAPTURE_CLI_DIST_RELATIVE_PATH =
  "bin/run-site-page-capture.mjs";
export const V1_COMPILE_CLI_DIST_RELATIVE_PATH = "bin/run-v1-compile.mjs";
export const V1_PLANS_CLI_DIST_RELATIVE_PATH = "bin/run-v1-plans.mjs";
export const COMPILE_EMAIL_CLI_DIST_RELATIVE_PATH = "bin/run-compile-email.mjs";
export const SANDBOX_SKILL_FILENAME = "SKILL.md";
// NOTE (Ezra, 2026-07-01, REPL-28638): machine-image root the snapshot COPYs
// the harness into (apps/daytona-snapshot Dockerfile). Distinct from
// SANDBOX_OPENCODE_PATH (`/workspace/.opencode`, the workspace skills mount) —
// the bundled CLI lives here, so the two are easy to confuse.
export const SANDBOX_MACHINE_OPENCODE_CONFIG_PATH = "/root/.config/opencode";
// NOTE (Andrew, 2026-07-16): opencode's sandbox-local data dir — opencode.db (session SQLite) lives here, not under /workspace.
export const SANDBOX_OPENCODE_DATA_PATH = "/root/.local/share/opencode";
// Absolute machine-image paths owned by opencode + the snapshot image
// (apps/daytona-snapshot Dockerfile), not the workspace layout.
export const SANDBOX_BUILTIN_SKILL_PATHS = [
  "/root/.opencode/skills",
  joinSandboxPath(SANDBOX_MACHINE_OPENCODE_CONFIG_PATH, "skills"),
] as const;
// Absolute paths the coordinator registry RPCs exec. Joined here (not in the
// harness build) so the coordinator stays off the esbuild graph.
export const SANDBOX_UPLOAD_REGISTRY_ITEMS_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  UPLOAD_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_INSTALL_REGISTRY_ITEMS_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  INSTALL_REGISTRY_ITEMS_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_PAGE_AUDIT_SCAN_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  PAGE_AUDIT_SCAN_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_SITE_PAGE_CAPTURE_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  SITE_PAGE_CAPTURE_CLI_DIST_RELATIVE_PATH,
);
export const SANDBOX_V1_COMPILE_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  V1_COMPILE_CLI_DIST_RELATIVE_PATH,
);
// The migrating-v1-pages skill's site compile, which the coordinator runs before a v1 hand-off's first turn.
export const SANDBOX_V1_COMPILE_SITE_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  "skills/migrating-v1-pages/scripts/compile-site.mjs",
);
// The migrating-v1-pages skill's site judge, which scores every page of a hand-off for `v1-migration-check`.
export const SANDBOX_V1_JUDGE_SITE_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  "skills/migrating-v1-pages/scripts/judge-site.mjs",
);
export const SANDBOX_V1_PLANS_CLI_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  V1_PLANS_CLI_DIST_RELATIVE_PATH,
);
// The migrating-v1-pages skill's plans-only hand-off, which run-v1-plans.mjs starts detached for a bedrock Workflow.
export const SANDBOX_V1_PLANS_SCRIPT_PATH = joinSandboxPath(
  SANDBOX_MACHINE_OPENCODE_CONFIG_PATH,
  "skills/migrating-v1-pages/scripts/plans.mjs",
);
// Where v1-migrate.mjs writes each page's bundle (`<dir>/<page slug>/component.json`, reference crops, brief).
export const SANDBOX_V1_MIGRATION_BUNDLES_DIR = "/tmp/v1-migration";
export const SANDBOX_PLANS_DIR = ".plans";
export const SANDBOX_BRANDING_DIR = sandboxProjectBrandingDirectory;
export const SANDBOX_DEFAULT_BRAND_DIR = joinSandboxPath(
  SANDBOX_BRANDING_DIR,
  "default",
);

// Namespace-relative Drive paths for durable dirs whose Drive folder name differs
// from the dotted workspace alias above. `files-changed` event paths are built by
// bedrock-api's `resolvePath`, which walks Drive folder *names*, so consumer globs
// and the coordinator's skill-restart check must match on THESE, not the dotted
// SANDBOX_*_DIR aliases.
//
// The Drive deliberately names these without a leading dot so they show in the
// Files app; the sandbox re-exposes them at the dotted paths via files-volume.sh
// symlinks (PERSISTED_DIRS: `.plans:plans`, `.opencode/skills:skills`). Project
// branding lives under the project's team context, and `resolvePath` strips only
// the structural `shared/` root — so its event paths keep the `.context/` prefix.
export const DRIVE_PLANS_DIR = "plans";
export const DRIVE_SKILLS_DIR = "skills";
// A home's `apps/` folder holds each mini-app's own data (Insights reports,
// Branding drafts), exposed in the sandbox at `/workspace/apps`.
export const DRIVE_APPS_DIR = "apps";
export const DRIVE_DEFAULT_BRAND_DIR = ".context/branding/default";

export const SANDBOX_DEV_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_DEV_DIR,
);
// Throwaway worktrees the coordinator rebases in, one per repository id, so the served tree never holds conflict markers.
export const SANDBOX_GIT_SYNC_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  ".git-sync",
);
export const SANDBOX_BRAND_STUDIO_DRAFTS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_BRAND_STUDIO_DRAFTS_DIR,
);
/** The brand documents from before the rows held them, beside the projection folder. */
export const LEGACY_BRAND_DOCUMENTS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_CONTEXT_DIR,
  "brand",
);
export const SANDBOX_INSIGHTS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_INSIGHTS_DIR,
);
export const SANDBOX_OPENCODE_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_OPENCODE_DIR,
);
export const SANDBOX_OPENCODE_SKILLS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_OPENCODE_SKILLS_DIR,
);
export const SANDBOX_PLANS_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PLANS_DIR,
);
export const SANDBOX_FILES_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_FILES_DIR,
);
export const SANDBOX_SHARED_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_SHARED_DIR,
);
export const SANDBOX_PROJECT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_DIR,
);
export const SANDBOX_USER_CONTEXT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_USER_CONTEXT_DIR,
);
export const SANDBOX_PROJECT_CONTEXT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_CONTEXT_DIR,
);
// The person's standing instructions to the agent: one file per `.context` scope.
export const SANDBOX_INSTRUCTIONS_FILENAME = "INSTRUCTIONS.md";
// Readers fall back to this name until the managing-memories skill renames the file.
export const SANDBOX_LEGACY_INSTRUCTIONS_FILENAME = "CONTEXT.md";
// Follow-up suggestions are written per person, not per project: they are
// drawn from that user's own sessions, so two members of one project should not
// overwrite each other -- which rules out `project/.context`. The project
// segment keeps them relevant, since "restock the SKUs that sold out" must not
// follow the user into an unrelated project.
// What a proactive pass found. Project-scoped, unlike suggestions: a finding
// about the store is about the business, so every member should see it.
export function sandboxProjectFindingsPath() {
  return joinSandboxPath(SANDBOX_PROJECT_CONTEXT_PATH, "FINDINGS.md");
}

export function sandboxUserSuggestionsPath({
  projectId,
}: {
  projectId: string;
}) {
  // Absolute, like the memory files: this path goes into a prompt telling the
  // agent where to write, and a relative one is ambiguous from a shell.
  return joinSandboxPath(
    SANDBOX_USER_CONTEXT_PATH,
    "suggestions",
    `${projectId}.md`,
  );
}

export const SANDBOX_BRANDING_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_BRANDING_DIR,
);
export const SANDBOX_DEFAULT_BRAND_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_DEFAULT_BRAND_DIR,
);

export const BRAND_JSON_FILENAME = "BRAND.json";
export const DETECTED_INTEGRATIONS_LEGACY_FILENAME = "integrations.json";
/** The rendered brand: the `GET /context` response written to disk after every write. Server-owned. */
export const SANDBOX_BRAND_JSON_PATH = joinSandboxPath(
  SANDBOX_DEFAULT_BRAND_PATH,
  BRAND_JSON_FILENAME,
);
/** The only names allowed under the brand folder; anything else there is legacy and swept. */
export const BRAND_FOLDER_ALLOWED_NAMES: ReadonlySet<string> = new Set([
  BRAND_JSON_FILENAME,
  "references",
  "personas",
]);
/** The two essays at the top of the team folder: written once at import when absent, then owned by the people on the project. */
export const BRAND_DOCUMENT_FILENAMES = {
  business: "BUSINESS.md",
  design: "DESIGN.md",
} as const;
export const SANDBOX_BUSINESS_DOCUMENT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_DIR,
  BRAND_DOCUMENT_FILENAMES.business,
);
export const SANDBOX_DESIGN_DOCUMENT_PATH = joinSandboxPath(
  SANDBOX_WORKSPACE_PATH,
  SANDBOX_PROJECT_DIR,
  BRAND_DOCUMENT_FILENAMES.design,
);
export const SANDBOX_BRAND_DOCUMENT_PATHS = {
  business: SANDBOX_BUSINESS_DOCUMENT_PATH,
  design: SANDBOX_DESIGN_DOCUMENT_PATH,
} as const;
/** How a person reads an absolute team-folder path (`/workspace/project/Notes.md` -> `Notes.md`); any other path is shown whole. */
export function teamFolderDisplayPath(path: string): string {
  const prefix = `${SANDBOX_PROJECT_PATH}/`;
  return path.startsWith(prefix) ? path.slice(prefix.length) : path;
}
/** The team's BUSINESS.md as a Drive row walk (`shared/BUSINESS.md`): the canonical business document readers rank first. */
export const DRIVE_BUSINESS_DOCUMENT_SEGMENTS = [
  SANDBOX_SHARED_DIR,
  BRAND_DOCUMENT_FILENAMES.business,
];
/** Where the September import left the essay copies; the sweep moves them to the team folder. */
export const LEGACY_BRAND_DOCUMENT_COPY_PATHS = {
  business: joinSandboxPath(
    SANDBOX_WORKSPACE_PATH,
    SANDBOX_PROJECT_CONTEXT_DIR,
    BRAND_DOCUMENT_FILENAMES.business,
  ),
  design: joinSandboxPath(
    SANDBOX_WORKSPACE_PATH,
    SANDBOX_PROJECT_CONTEXT_DIR,
    BRAND_DOCUMENT_FILENAMES.design,
  ),
} as const;
const LEGACY_PROJECTION_FILENAMES = [
  "BUSINESS.md",
  "DESIGN.md",
  "fonts.json",
  "logo.json",
  "assets.json",
  "manifest.json",
  "project-context.md",
  "business-state.md",
  DETECTED_INTEGRATIONS_LEGACY_FILENAME,
] as const;
/** Every path an earlier brand layout wrote: swept on every brand write and refused by the file service, so no writer brings an old layout back. */
export const LEGACY_BRAND_PATHS: readonly string[] = [
  ...LEGACY_PROJECTION_FILENAMES.map((filename) => {
    return joinSandboxPath(SANDBOX_DEFAULT_BRAND_PATH, filename);
  }),
  joinSandboxPath(
    LEGACY_BRAND_DOCUMENTS_PATH,
    BRAND_DOCUMENT_FILENAMES.business,
  ),
  joinSandboxPath(LEGACY_BRAND_DOCUMENTS_PATH, BRAND_DOCUMENT_FILENAMES.design),
  LEGACY_BRAND_DOCUMENTS_PATH,
  SANDBOX_BRAND_STUDIO_DRAFTS_PATH,
];
// NOTE (Ezra, 2026-06-30, REPL-28543): per-site key for a shared page — the same
// route in sibling sites of one project must not collide on one registry row.
// The editor (record lookup) and the upload tool (write) both build it here so
// the keys can't drift; `siteDirectory` is one dir segment (e.g. `website-2`).
export function registryPageRouteSourceKey({
  siteDirectory,
  route,
}: {
  siteDirectory: string;
  route: string;
}): string {
  // `|| "/"` (not `??`): a bare "/" route strips to "" and must fall back to "/".
  const normalizedRoute = route.replace(/\/+$/u, "") || "/";
  return `${siteDirectory}:${normalizedRoute}`;
}

export function getSandboxWorkspaceRelativePath(path: string): string | null {
  if (path === SANDBOX_WORKSPACE_PATH) {
    return "";
  }
  const workspacePrefix = `${SANDBOX_WORKSPACE_PATH}/`;
  return path.startsWith(workspacePrefix)
    ? path.slice(workspacePrefix.length)
    : null;
}

// Deterministic Drive name for a chat upload, `<slug>-<fileId><ext>`, so the uploader and every reader derive the same path from the fileId alone.
export function buildChatUploadFilename({
  filename,
  fileId,
}: {
  filename: string;
  fileId: string;
}): string {
  const lastDot = filename.lastIndexOf(".");
  const hasExt = lastDot > 0 && lastDot < filename.length - 1;
  const stem = hasExt ? filename.slice(0, lastDot) : filename;
  const ext = hasExt ? filename.slice(lastDot).toLowerCase() : "";
  const slug = slugify(stem);
  return slug ? `${slug}-${fileId}${ext}` : `${fileId}${ext}`;
}

export function getChatUploadPath(file: {
  filename: string;
  fileId: string;
}): string {
  return joinSandboxPath(SANDBOX_FILES_PATH, buildChatUploadFilename(file));
}

/**
 * The published brand folder as a mount-relative path — the projection the
 * project context renders on every publish. Everything under it is generated,
 * except the two files other owners write there (the integrations detector
 * and the setup facts). One predicate for the server freeze, the context
 * pointers and Branding, so a legacy draft is never mistaken for it.
 */
export const PROJECTION_FOLDER_RELATIVE_PATH = ".context/branding/default";
const BRAND_FOLDER_RELATIVE_PATH = ".context/branding";

/**
 * The website reference folders the brand import writes: one folder per
 * site under `<host>/` with `index.json`, `pages/<page>/` (screenshots and
 * styled markup) and `sections/<page>/<nn>-<slug>/` (each section's crop and
 * styled markup per device). Generated, so frozen like the projection: only
 * the import writes or deletes anything under it.
 */
export const BRAND_REFERENCES_FOLDER_RELATIVE_PATH =
  ".context/branding/default/references";
export const SANDBOX_BRAND_REFERENCES_PATH = joinSandboxPath(
  SANDBOX_DEFAULT_BRAND_PATH,
  "references",
);
/** Where the references lived before they moved under the brand; the sweep reparents it. */
export const LEGACY_BRAND_REFERENCES_PATH = joinSandboxPath(
  SANDBOX_BRANDING_PATH,
  "references",
);

// A team path loses `/workspace/project/` or `shared/`; a home path (the drafts
// folder) loses `/workspace/` so it reads like the Drive reports it (`apps/…`).
function contextRelativeOf(path: string): string {
  return path
    .replace(/^\/+/u, "")
    .replace(/^workspace\/project\//u, "")
    .replace(/^workspace\//u, "")
    .replace(/^shared\//u, "");
}

/** True for the references folder itself and anything inside it (mount-relative, `/workspace/project/…` or `shared/…`). */
export function isBrandReferencePath(path: string | null): boolean {
  if (!path) {
    return false;
  }
  const relative = contextRelativeOf(path);
  return (
    relative === BRAND_REFERENCES_FOLDER_RELATIVE_PATH ||
    relative.startsWith(`${BRAND_REFERENCES_FOLDER_RELATIVE_PATH}/`)
  );
}

/** True under .context/branding/ (mount-relative, /workspace/project/… or shared/…), which is server-owned; false for the folder itself. */
export function isBrandFolderPath(path: string | null): boolean {
  if (!path) {
    return false;
  }
  const relative = contextRelativeOf(path);
  return relative.startsWith(`${BRAND_FOLDER_RELATIVE_PATH}/`);
}

// The essay copies are refused too: the sweep moves them, so a write there would be lost on the next brand write.
const REFUSED_LEGACY_PATHS: readonly string[] = [
  ...LEGACY_BRAND_PATHS,
  ...Object.values(LEGACY_BRAND_DOCUMENT_COPY_PATHS),
];

/** True for a path an earlier brand layout wrote (`LEGACY_BRAND_PATHS` and the essay copies), or anything inside one of its folders. */
export function isLegacyBrandPath(path: string | null): boolean {
  if (!path) {
    return false;
  }
  const relative = contextRelativeOf(path);
  return REFUSED_LEGACY_PATHS.some((legacy) => {
    const candidate = contextRelativeOf(legacy);
    return relative === candidate || relative.startsWith(`${candidate}/`);
  });
}
